'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faLocationDot,
  faPlus,
} from '@fortawesome/pro-solid-svg-icons';
import { PulseIconButton } from 'src/components/PulseUI';
import { getProfileUrl } from 'src/utils/profileUrl';
import {
  AVATAR_CLASSES,
  CONNECTION_META,
  FALLBACK_CONNECTION_META,
} from '../constants';
import type { SimilarProvider } from '../types';

interface SimilarProviderCardProps {
  provider: SimilarProvider;
  onAddToList: (provider: SimilarProvider) => void;
}

/** First and last initial of a display name, e.g. "James O. Feldman" -> "JF" */
const getNameInitials = (name?: string | null): string => {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';

  const first = words[0].charAt(0);
  const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';

  return `${first}${last}`.toUpperCase();
};

/**
 * Deterministic avatar color so a provider keeps the same swatch between
 * renders — derived from the id rather than the list position.
 */
const getAvatarClass = (providerId?: string): string => {
  const id = String(providerId ?? '');
  const sum = Array.from(id).reduce((acc, char) => acc + char.charCodeAt(0), 0);

  return AVATAR_CLASSES[sum % AVATAR_CLASSES.length];
};

const SimilarProviderCard = ({
  provider,
  onAddToList,
}: SimilarProviderCardProps) => {
  const accentMeta =
    CONNECTION_META[provider?.primary_connection] ?? FALLBACK_CONNECTION_META;

  const location = useMemo(
    () => [provider?.city, provider?.state].filter(Boolean).join(', '),
    [provider?.city, provider?.state]
  );

  const profileUrl = getProfileUrl(
    provider?.type || 'hcp',
    provider?.provider_id
  );

  return (
    <div
      className="flex flex-none flex-col w-72 max-h-[21rem] bg-white border border-neutral-200 rounded-lg overflow-hidden transition-shadow hover:shadow-md"
      data-testid={`similar-provider-card-${provider?.provider_id}`}
    >
      <div
        className={`h-1 w-full ${accentMeta.accentClassName}`}
        aria-hidden="true"
      />

      <div className="flex flex-col flex-1 min-h-0 gap-3 p-4">
        <div className="flex items-center gap-3">
          <div
            className={`flex flex-none items-center justify-center w-10 h-10 rounded-full text-white text-sm font-bold ${getAvatarClass(
              provider?.provider_id
            )}`}
            aria-hidden="true"
          >
            {getNameInitials(provider?.name)}
          </div>
          <div className="min-w-0">
            <p
              className="text-sm font-bold text-primary truncate"
              title={provider?.name ?? ''}
              data-testid="similar-provider-name"
            >
              {provider?.name}
            </p>
            {provider?.specialty && (
              <p className="text-xs font-medium text-secondary truncate">
                {provider.specialty}
              </p>
            )}
            {location && (
              <p className="flex items-center gap-1 text-xs text-tertiary truncate">
                <Icon icon={faLocationDot} className="w-3 h-3 flex-none" />
                {location}
              </p>
            )}
          </div>
        </div>

        {/*
          The metric and the reason are two different kinds of claim — a count and a
          shared quality — so they get their own rows rather than sharing a wrapping
          caption. `items-baseline` keeps the figure locked to its own unit label:
          centering it against a two-line caption floated it between the two, reading
          as if it belonged to neither. With one row the tile is unchanged, so cards
          without a reason look exactly as they did.
        */}
        {(provider?.metric_value ?? null) !== null && (
          <div
            className="flex flex-col gap-1 px-3 py-2 bg-neutral-100 rounded-lg"
            data-testid="similar-provider-reason"
          >
            <div className="flex items-baseline gap-2">
              <span className="text-base font-extrabold text-primary">
                {provider.metric_value}
              </span>
              <span className="text-xs text-tertiary leading-tight">
                {provider?.metric_label}
              </span>
            </div>
            {provider?.reason && (
              <p
                className="pt-1 border-t border-neutral-200 text-xs text-tertiary leading-tight"
                data-testid="similar-provider-reason-text"
              >
                {provider.reason}
              </p>
            )}
          </div>
        )}

        {/*
          The pills are the only part of the card that grows with the data, so they
          absorb the card's height cap: rows past it scroll here rather than making
          every card in the row taller (the carousel stretches cards to the tallest).
          That leaves ~143px for pills under the root's max-h-[21rem], or six rows.
          The cap is an arbitrary value because Tailwind's scale jumps 320px -> 384px,
          and 384px puts the card back in the too-tall territory this fixes.
          `content-start` keeps the rows their natural height instead of stretching
          them to fill the leftover space on cards with few connections.
        */}
        <div
          className="flex flex-wrap content-start flex-1 min-h-0 gap-1.5 overflow-y-auto"
          data-testid="similar-provider-connections"
        >
          {provider?.connections?.map((connection) => {
            const meta =
              CONNECTION_META[connection?.type] ?? FALLBACK_CONNECTION_META;

            return (
              <span
                key={`${provider.provider_id}-${connection?.type}-${connection?.label}`}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[0.625rem] font-bold leading-none ${meta.pillClassName}`}
                data-testid={`similar-provider-pill-${connection?.type}`}
              >
                <Icon icon={meta.icon} className="w-3 h-3" />
                {connection?.label || meta.label}
              </span>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between px-4 py-2 border-t border-neutral-200">
        <Link
          href={profileUrl}
          className="inline-flex items-center gap-1 text-xs font-semibold text-link hover:underline"
          data-testid="similar-provider-view-profile"
        >
          View profile
          <Icon icon={faArrowRight} className="w-3 h-3" />
        </Link>
        <PulseIconButton
          icon={faPlus}
          variant="outline"
          color="neutral"
          size="sm"
          onClick={() => onAddToList(provider)}
          tooltip="Add to List"
          aria-label={`Add ${provider?.name ?? 'provider'} to list`}
          data-testid="similar-provider-add-to-list"
        />
      </div>
    </div>
  );
};

export default SimilarProviderCard;
